<script setup>
import { computed } from "vue";
import { Icon } from "@iconify/vue";
import allowlist from "./icon-allowlist.js";

const props = defineProps({ name: { type: String, required: true } });

// Accept both `circle-help` and `lucide:circle-help`. This component adds the
// collection prefix itself, so a caller that passed the Iconify-style name it
// uses with <Icon> everywhere else produced `lucide:lucide:…`, which resolves to
// nothing and renders an empty box — 28 confirm dialogs across the KB shipped
// with a blank icon slot for exactly this reason.
const canonical = computed(() => props.name.replace(/^lucide:/, ""));

// `:ssr="true"` starts Icon's `rendering` ref at true (@iconify/vue 5: `ref(!!props.ssr)`).
// Without it the component waits for onMounted before it will look at icon data, so
// server-rendered markup carries `<svg viewBox="0 0 16 16"></svg>` with no content — which is
// how the WordPress shell shipped two invisible 18x18 clickable spans beside the sign form's
// step headings, collecting dead clicks. On the client it also drops the one-tick empty frame.
// It must be bound, not written bare: Icon declares its props as a plain name array, so Vue
// applies no boolean casting and the shorthand `ssr` arrives as "", which `!!` reads as false.

// Dev guard: lucide-only, and flag names we have not vetted so agents/devs
// can't silently introduce off-canon or non-resolving icons.
if (import.meta.env.DEV && !allowlist.includes(canonical.value)) {
    console.warn(`[BaseIcon] "${props.name}" is not in the icon allowlist — add it to icon-allowlist.js or pick a canonical name.`);
}
</script>

<template>
    <Icon
        :icon="`lucide:${canonical}`"
        :ssr="true" />
</template>
